/**
 * Advertising and analytics consent.
 *
 * **Corrected 2026-09-18** — an earlier version of this comment said Google
 * Analytics was not gated by this banner at all. As of 2026-09-18 it is: this
 * one banner now governs both the *ad slot request* (via `hasAdConsent()`)
 * and the Consent Mode `analytics_storage` signal (via
 * `pushConsentModeUpdate` in `src/lib/ads.ts`), from the same answer. It
 * renders nothing at all unless advertising or analytics is configured *and*
 * the user has not already answered *and* no browser-level opt-out (DNT /
 * GPC) is present. gtag.js's own *script load* still happens unconditionally
 * (see `src/lib/ga.ts`) — what this banner controls is whether it may use a
 * cookie once loaded.
 *
 * ## Design decisions that are policy, not taste
 *
 * **Reject is as easy as accept.** Both are real buttons of equal weight, side
 * by side. A banner where "Accept" is a filled button and "Reject" is a grey
 * link in the corner is a dark pattern, and under GDPR consent obtained that way
 * is not freely given. It is also increasingly enforced against.
 *
 * **It says what is actually collected.** "We value your privacy" says nothing.
 * This names Google, says cookies, and says what they are for.
 *
 * ## Limitation, stated plainly — corrected 2026-08-28
 *
 * This is a first-party gate, not an IAB TCF v2.2 Consent Management Platform.
 * Google requires a certified CMP for EEA/UK/Swiss traffic. This component is
 * the choke point such a CMP would drive; it is not a substitute for one. See
 * docs/privacy-consent.md before serving ads to those regions.
 *
 * **Correction:** an earlier version of this comment (and of the banner's own
 * copy, and of `/privacy` and `/terms`) claimed nothing loads and zero
 * third-party requests occur before this banner is answered. That was wrong.
 * `scripts/prerender.ts` injects the AdSense base script into the static
 * `<head>` of every page except `/resume-builder`, unconditionally — verified
 * live: the script executes and pings Google's ad-traffic-quality endpoint
 * (`ep1.adtrafficquality.google/pagead/sodar`) on page load, before any answer
 * exists. What this banner and `hasAdConsent()` actually gate is the *ad slot
 * request* (`pushAdSlot()` in `src/lib/ads.ts`) and the Consent Mode ad
 * signals — not the base script's own load or its own verification traffic.
 * Live-verified 2026-08-28; see `docs/privacy-consent.md` ("What consent does
 * not gate").
 */

import { useEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Cookie } from 'lucide-react'
import {
  consentApplies,
  effectiveConsent,
  hasGlobalOptOut,
  openConsentChoices,
  readConsent,
  shouldAskConsent,
  syncConsentMode,
  writeConsent,
} from '@/lib/ads'
import type { ConsentState } from '@/lib/ads'

/**
 * The answer to show the visitor: what they chose, or, with no answer stored,
 * what applies to them anyway. Only differs from `readConsent()` in `regional`
 * consent mode, where an unanswered visitor outside the consent regions is
 * opted in (src/config/consent.ts).
 */
const shownConsent = (): ConsentState => {
  const stored = readConsent()
  return stored === 'unset' && effectiveConsent() === 'granted' ? 'granted' : stored
}

const CURRENT_LABEL: Record<ConsentState, string> = {
  granted: 'Your current choice: Allow.',
  denied: 'Your current choice: Decline.',
  unset: '',
}

export const ConsentBanner = () => {
  const [visible, setVisible] = useState(false)
  /** Opened on request (footer "Cookie choices"), not because no answer exists. */
  const [requested, setRequested] = useState(false)
  const [current, setCurrent] = useState<ConsentState>('unset')
  const dialogRef = useRef<HTMLDivElement>(null)
  const { pathname } = useLocation()

  /*
   * Never asked unprompted on the builder, and not only to stay out of the way.
   *
   * Ads have never been shown inside the builder, so an unprompted question
   * there asks about something that cannot occur on that page.
   *
   * It also fixes a real obstruction. The banner is fixed to the bottom of the
   * viewport, and on a phone the builder puts its pane switcher in exactly that
   * space — the banner sat on top of the primary control. Caught by the browser
   * tests, which could no longer click it.
   *
   * When the visitor asks for it through "Cookie choices" (2026-10-21) it opens
   * on every page, the builder included: they asked, and analytics does run
   * there.
   */
  const onBuilder = pathname.startsWith('/resume-builder')

  useEffect(() => {
    // After mount only: the decision depends on localStorage and navigator,
    // neither of which exists during the prerender.
    syncConsentMode()
    setVisible(!onBuilder && shouldAskConsent())
    const open = () => {
      if (!consentApplies() || hasGlobalOptOut()) return
      setCurrent(shownConsent())
      setRequested(true)
      setVisible(true)
    }
    window.addEventListener('ats:consent-open', open)
    return () => window.removeEventListener('ats:consent-open', open)
  }, [onBuilder])

  // A keyboard or screen-reader user who opened it from the footer lands on it.
  useEffect(() => {
    if (visible && requested) dialogRef.current?.focus()
  }, [visible, requested])

  /*
   * The banner is fixed to the bottom of the viewport, so while it is open it
   * used to cover the last strip of the page: at the end of any page the footer
   * links (Privacy, Terms, Cookie choices) sat underneath it and could not be
   * clicked or seen when focused (found 2026-10-21). Padding the body by the
   * banner's height lets the page scroll clear of it.
   */
  useEffect(() => {
    const el = dialogRef.current
    if (!visible || !el) return
    const body = document.body
    const pad = () => {
      body.style.paddingBottom = `${el.offsetHeight}px`
    }
    pad()
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(pad)
    observer?.observe(el)
    return () => {
      observer?.disconnect()
      body.style.paddingBottom = ''
    }
  }, [visible])

  if (!visible) return null

  const answer = (state: 'granted' | 'denied') => {
    writeConsent(state)
    setVisible(false)
    setRequested(false)
  }

  return (
    <div
      ref={dialogRef}
      tabIndex={-1}
      role="dialog"
      aria-modal="false"
      aria-labelledby="consent-title"
      className="anim-slide-up fixed inset-x-0 bottom-0 z-[70] border-t border-base-300 bg-base-100 p-4 shadow-2xl outline-none"
    >
      <div className="mx-auto flex max-w-4xl flex-col gap-3 sm:flex-row sm:items-center">
        <Cookie size={20} className="shrink-0 text-primary" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <h2 id="consent-title" className="text-sm font-semibold">
            Allow analytics and advertising?
          </h2>
          <p className="mt-1 text-sm opacity-80">
            If you allow it, Google sets cookies here for site analytics and ads, and Ahrefs Web
            Analytics counts your visits. Decline and no analytics cookie is set, Google is told not
            to personalise ads, Ahrefs is not loaded, and the ad spaces this site places stay empty. Google
            Analytics still receives a cookieless ping either way. <strong>Your resume is never
            sent</strong>: it stays in your browser. Change this any time with Cookie choices at the
            bottom of the page; the <Link to="/privacy" className="link">privacy page</Link> lists
            every service.
          </p>
          {requested && CURRENT_LABEL[current] && (
            <p className="mt-1 text-sm font-medium">{CURRENT_LABEL[current]}</p>
          )}
        </div>
        {/* Equal weight. Consent is not freely given if refusing is harder. */}
        <div className="flex shrink-0 gap-2">
          <button type="button" className="btn btn-outline btn-sm flex-1" onClick={() => answer('denied')}>
            Decline
          </button>
          <button type="button" className="btn btn-primary btn-sm flex-1" onClick={() => answer('granted')}>
            Allow
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * Footer "Cookie choices" (added 2026-10-21), on every page. Reopens the banner
 * with the current answer shown. A visitor whose browser sends Do Not Track or
 * Global Privacy Control cannot be asked, so they are taken to the privacy page
 * section that explains why instead of to a banner that would ignore them.
 */
export const CookieChoicesButton = ({ className }: { className?: string }) => {
  const navigate = useNavigate()
  if (!consentApplies()) return null
  return (
    <button
      type="button"
      className={className}
      onClick={() => (hasGlobalOptOut() ? navigate('/privacy#your-choice') : openConsentChoices())}
    >
      Cookie choices
    </button>
  )
}

const CHOICE_LABEL: Record<ConsentState, string> = {
  granted: 'You allowed analytics and advertising.',
  denied: 'You declined analytics and advertising.',
  unset: 'You have not answered the question yet.',
}

/**
 * The "change your choice" control on /privacy. Reads the stored answer after
 * mount only: localStorage and navigator do not exist in the prerender, so the
 * static HTML shows the neutral state.
 */
export const ConsentChoice = () => {
  const [state, setState] = useState<ConsentState | null>(null)
  const [optOut, setOptOut] = useState(false)

  useEffect(() => {
    const refresh = () => {
      setOptOut(hasGlobalOptOut())
      setState(shownConsent())
    }
    refresh()
    window.addEventListener('ats:consent', refresh)
    return () => window.removeEventListener('ats:consent', refresh)
  }, [])

  if (optOut) {
    return (
      <p>
        Your browser sends Do Not Track or Global Privacy Control, so this site treats you as having
        declined and does not ask. Turn that setting off if you want to be asked.
      </p>
    )
  }

  return (
    <div className="flex flex-wrap items-center gap-3">
      <p className="text-sm" role="status">
        {state ? CHOICE_LABEL[state] : 'Your current choice is stored in this browser.'}
      </p>
      <button type="button" className="btn btn-outline btn-sm" onClick={openConsentChoices}>
        Change your choice
      </button>
    </div>
  )
}
